import { useState, type FormEvent } from "react";
import { api, formatDateTime } from "../api";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import Dialog from "../components/Dialog";
import { EmptyState, ErrorState, MetricCard, PageHeader, PageSkeleton } from "../components/Ui";
import { NoClient } from "../components/Workflow";
import "../polish/ai-search.css";

type Engine = "chatgpt" | "gemini" | "claude";
type Prompt = { id: number; area: string | null; prompt: string; active: boolean; source: string };
type Run = { id: number; status: string; answers: number; named: number; errors: string[]; started_at: string; finished_at: string | null };
type Result = { id: number; run_id: number; prompt_id: number; engine: Engine; named: boolean | null; competitors: string[]; sources: { url: string; title: string | null }[]; answer: string | null; error: string | null; created_at: string };
type Data = { engines: Record<Engine, string>; prompts: Prompt[]; runs: Run[]; results: Result[] };
const ORDER: Engine[] = ["chatgpt", "gemini", "claude"];

/**
 * AI search (Shawn, 2026-10-28: "AEO, AI prompting"): whether ChatGPT, Gemini and
 * Claude name the client when a buyer asks them for help nearby, and who they name
 * instead. Questions run weekly once someone has run them here the first time.
 */
export default function AiSearch() {
  const { clientId, client } = useClientSelection();
  return <>
    <PageHeader title="AI search" description={`Whether ChatGPT, Gemini and Claude name ${client?.name || "the client"} when a buyer asks them for help nearby.`} />
    {clientId ? <AiSearchView key={clientId} clientId={clientId} /> : <NoClient />}
  </>;
}

function AiSearchView({ clientId }: { clientId: number }) {
  const { user } = useAuth();
  const canWrite = ["superadmin", "exec"].includes(user?.role || "");
  const { data, loading, error, reload } = useApi<Data>(`/api/ai-search/${clientId}`);
  const [busy, setBusy] = useState(""), [notice, setNotice] = useState<{ text: string; ok: boolean } | null>(null);
  const [open, setOpen] = useState<{ prompt: Prompt; result: Result } | null>(null);
  const [draft, setDraft] = useState(""), [area, setArea] = useState("");
  if (loading && !data) return <PageSkeleton cards={3} />;
  if (error || !data) return <ErrorState message={error || "AI search could not load."} onRetry={reload} />;

  // The latest run that got answers; a run where no engine answered is explained, not shown as 0 of 0.
  const latestRun = data.runs.find((r) => r.status !== "running" && r.answers > 0);
  const lastTry = data.runs.find((r) => r.status !== "running");
  const emptyTry = lastTry && lastTry.answers === 0 ? lastTry : null;
  const active = data.prompts.filter((p) => p.active);
  const cell = (p: Prompt, e: Engine) => data.results.find((r) => r.prompt_id === p.id && r.engine === e);
  const latest = latestRun ? data.results.filter((r) => r.run_id === latestRun.id) : [];
  const perEngine = (e: Engine) => { const rows = latest.filter((r) => r.engine === e && r.answer); return { named: rows.filter((r) => r.named).length, of: rows.length }; };
  // Who the engines name instead, across this run's answers, most often first.
  const rivals = Object.entries(latest.flatMap((r) => r.competitors).reduce<Record<string, number>>((m, n) => ({ ...m, [n]: (m[n] || 0) + 1 }), {}))
    .sort((a, b) => b[1] - a[1]).slice(0, 5);

  async function run() {
    setBusy("run"); setNotice(null);
    try {
      const r = await api<{ answers: number; named: number; asked?: number; errors?: string[]; message?: string }>(`/api/ai-search/${clientId}/run`, { method: "POST", body: "{}" });
      setNotice(r.message ? { ok: true, text: r.message }
        : !r.answers ? { ok: false, text: `No answers this time. ${r.errors?.join(" ") || ""}`.trim() }
        : { ok: true, text: `Named in ${r.named} of ${r.answers} answers.${r.errors?.length ? ` Not answered: ${r.errors.join(" ")}` : ""}` });
      reload();
    } catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "The run could not finish." }); }
    finally { setBusy(""); }
  }
  async function add(e: FormEvent) {
    e.preventDefault(); setBusy("add"); setNotice(null);
    try { await api(`/api/ai-search/${clientId}/prompts`, { method: "POST", body: JSON.stringify({ prompt: draft, area }) }); setDraft(""); setArea(""); reload(); }
    catch (err) { setNotice({ ok: false, text: err instanceof Error ? err.message : "The question could not be saved." }); }
    finally { setBusy(""); }
  }
  async function toggle(p: Prompt) {
    setBusy(`p${p.id}`); setNotice(null);
    try { await api(`/api/ai-search/${clientId}/prompts/${p.id}`, { method: "PUT", body: JSON.stringify({ active: !p.active }) }); reload(); }
    catch (err) { setNotice({ ok: false, text: err instanceof Error ? err.message : "The question could not be changed." }); }
    finally { setBusy(""); }
  }

  return <div className="ai-search">
    <div className="ai-search-bar">
      <span className="muted">{latestRun ? `Last run ${formatDateTime(latestRun.finished_at || latestRun.started_at)}. Runs again weekly.` : emptyTry ? "No answers yet. Runs again weekly." : "Not run yet. The first run turns on weekly runs."}</span>
      {canWrite && <button type="button" className="button" disabled={!!busy || !active.length} onClick={() => void run()}>{busy === "run" ? "Asking ChatGPT, Gemini and Claude…" : "Run now"}</button>}
    </div>
    {notice && <p className={`inline-notice ${notice.ok ? "success" : "danger"}`} role={notice.ok ? "status" : "alert"}>{notice.text}</p>}
    {!notice && emptyTry && <p className="inline-notice danger">The last run on {formatDateTime(emptyTry.started_at)} got no answers. {emptyTry.errors.join(" ")}</p>}

    {latestRun && <div className="module-metrics">
      <MetricCard label="Named" value={`${latestRun.named} of ${latestRun.answers}`} detail="Answers in the last run that named the client" />
      {ORDER.map((e) => { const s = perEngine(e); return <MetricCard key={e} label={data.engines[e]} value={s.of ? `${s.named} of ${s.of}` : "—"} detail={s.of ? "Answers that named the client" : "No answers in the last run"} />; })}
    </div>}

    <section className="panel">
      <div className="panel-heading"><h2>Questions</h2><span className="panel-meta">Each one is asked of all three with web search on, about 3 to 4 cents a search</span></div>
      {data.prompts.length ? <div className="table-wrap"><table className="workflow-table ai-search-table">
        <thead><tr><th>Question</th>{ORDER.map((e) => <th key={e}>{data.engines[e]}</th>)}<th><span className="sr-only">Actions</span></th></tr></thead>
        <tbody>{data.prompts.map((p) => <tr key={p.id} className={p.active ? undefined : "is-off"}>
          <td>{p.prompt}{p.area && <small className="muted">{p.area}</small>}</td>
          {ORDER.map((e) => { const r = cell(p, e); return <td key={e} data-label={data.engines[e]}>
            {!r ? <span className="muted">Not asked</span>
              : r.error ? <span className="muted" title={r.error}>No answer</span>
              : <button type="button" className={`ai-search-cell ${r.named ? "is-named" : ""}`} onClick={() => setOpen({ prompt: p, result: r })}>{r.named ? "Named" : "Not named"}</button>}
          </td>; })}
          <td className="ai-search-actions">{canWrite && <button type="button" className="text-button" disabled={!!busy} onClick={() => void toggle(p)}>{p.active ? "Turn off" : "Turn on"}</button>}</td>
        </tr>)}</tbody>
      </table></div> : <EmptyState title="No questions yet" description="Add the questions a buyer would ask, like: who is the best junk removal company in Fresno?" />}
      {canWrite && <form className="ai-search-add" onSubmit={(e) => void add(e)}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={300} placeholder="Add a question a buyer would ask" aria-label="New question" />
        <input value={area} onChange={(e) => setArea(e.target.value)} maxLength={80} placeholder="Area (optional)" aria-label="Area" />
        <button type="submit" className="button secondary" disabled={!!busy || draft.trim().length < 5}>{busy === "add" ? "Adding…" : "Add question"}</button>
      </form>}
    </section>

    {rivals.length > 0 && <section className="panel">
      <div className="panel-heading"><h2>Named instead</h2><span className="panel-meta">Businesses the answers named in the last run</span></div>
      <ul className="ai-search-rivals">{rivals.map(([name, n]) => <li key={name}><span>{name}</span><span className="muted">{n} {n === 1 ? "answer" : "answers"}</span></li>)}</ul>
    </section>}

    {open && <Dialog title={open.prompt.prompt} description={`${data.engines[open.result.engine]}, ${formatDateTime(open.result.created_at)}`} onClose={() => setOpen(null)}>
      <div className="ai-search-answer">
        <p className={open.result.named ? "inline-notice success" : "inline-notice"}>{open.result.named ? "This answer named the client." : "This answer did not name the client."}</p>
        {open.result.competitors.length > 0 && <p><strong>Named:</strong> {open.result.competitors.join(", ")}</p>}
        <p className="ai-search-text">{open.result.answer}</p>
        {open.result.sources.length > 0 && <><h3>Sources</h3><ul>{open.result.sources.map((s) => <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer">{s.title || new URL(s.url).hostname}</a></li>)}</ul></>}
      </div>
    </Dialog>}
  </div>;
}
